import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UseCopilot } from './useCopilot'
import CopilotHeader from './CopilotHeader'
import HistoryDrawer from './HistoryDrawer'
import MessageList from './MessageList'
import VoicePanel from './VoicePanel'
import Composer from './Composer'
import { SparkIcon } from './icons'
import { cx } from './cx'
import styles from './index.module.scss'

interface CopilotWindowProps {
  open: boolean
  copilot: UseCopilot
  onClose: () => void
}

const CopilotWindow: React.FC<CopilotWindowProps> = ({ open, copilot, onClose }) => {
  const { t } = useTranslation()
  const [historyOpen, setHistoryOpen] = useState(false)
  const { refreshHistory } = copilot

  /* pull the server-side conversation list whenever the drawer opens
     (also reconciles any optimistic new-chat stub with the persisted row) */
  useEffect(() => {
    if (historyOpen) refreshHistory()
  }, [historyOpen, refreshHistory])

  const startNew = () => {
    copilot.newChat()
    setHistoryOpen(false)
  }
  const pickSession = (id: string) => {
    copilot.resumeSession(id)
    setHistoryOpen(false)
  }

  return (
    <div className={cx(styles.window, open && styles.windowOpen)}>
      <CopilotHeader
        status={copilot.status}
        historyOpen={historyOpen}
        onToggleHistory={() => setHistoryOpen((o) => !o)}
        onNewChat={startNew}
        onClose={onClose}
      />

      <div className={styles.body}>
        <HistoryDrawer
          open={historyOpen}
          sessions={copilot.sessions}
          activeId={copilot.activeSessionId}
          loading={copilot.historyLoading}
          error={copilot.historyError}
          onSelect={pickSession}
          onNew={startNew}
          onDelete={copilot.deleteSession}
          onClose={() => setHistoryOpen(false)}
        />

        <div className={styles.main}>
          <MessageList messages={copilot.messages} steps={copilot.steps} thinking={copilot.thinking} />

          <div className={styles.dock}>
            <div className={styles.powered}>
              <span className={styles.poweredLine} />
              <span className={styles.poweredLabel}>
                <SparkIcon />
                {t('genAI.copilot-powered')}
              </span>
              <span className={styles.poweredLine} />
            </div>

            {copilot.error && <div className={styles.errorNote}>{t(`genAI.${copilot.error}`)}</div>}

            <VoicePanel />
            <Composer onSend={(text) => copilot.send(text)} busy={copilot.thinking} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default CopilotWindow
